import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from 'lucide-react'

/**
 * Toast, lightweight notification system.
 *
 * Usage:
 *   const toast = useToast()
 *   toast.success('Vote started')
 *   toast.error('Could not save: ' + err.message)
 *   toast.info('Reconnected')
 *
 * Wrap the app in <ToastProvider> once. Toasts auto-dismiss after 4.5s.
 * Styled to match the warm-pastel design-system family.
 */
const ToastContext = createContext(null)

const ICONS = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
}

const TONE = {
  success: { bg: '#e7f3eb', border: '#bce0c8', text: '#2f7d4f', accent: '#2f7d4f' },
  error:   { bg: '#fbeae6', border: '#f0c9c0', text: '#b3321c', accent: '#b3321c' },
  warning: { bg: '#fbf1e1', border: '#ecd9ad', text: '#b87514', accent: '#b87514' },
  info:    { bg: '#eef4fb', border: '#cbdcf0', text: '#1f5fa8', accent: '#1f5fa8' },
}

export function ToastProvider ({ children }) {
  const [toasts, setToasts] = useState([])
  const timers = useRef(new Map())

  useEffect(() => () => {
    timers.current.forEach(timer => clearTimeout(timer))
    timers.current.clear()
  }, [])

  const push = useCallback((kind, message, opts = {}) => {
    const id = Math.random().toString(36).slice(2)
    // Toasts with an undo action get a longer default window (10s) so the
    // user has time to react before the action becomes permanent.
    const ttl = opts.ttl ?? (opts.action ? 10000 : 4500)
    setToasts(prev => [...prev, {
      id, kind, message, action: opts.action || null, ts: Date.now(), expiresAt: ttl > 0 ? Date.now() + ttl : null,
    }])
    if (ttl > 0) {
      const timer = setTimeout(() => {
        timers.current.delete(id)
        setToasts(prev => prev.filter(t => t.id !== id))
      }, ttl)
      timers.current.set(id, timer)
    }
    return id
  }, [])

  const dismiss = useCallback(id => {
    const timer = timers.current.get(id)
    if (timer) clearTimeout(timer)
    timers.current.delete(id)
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  // Keep the context value stable when a toast is shown. Otherwise every
  // useToast consumer (including the live admin room) re-renders per notice.
  const api = useMemo(() => ({
    success: (msg, opts) => push('success', msg, opts),
    error:   (msg, opts) => push('error', msg, opts),
    warning: (msg, opts) => push('warning', msg, opts),
    info:    (msg, opts) => push('info', msg, opts),
    // Undo strip, emits a neutral toast with a "Rückgängig" button.
    // Pass { onUndo } as second arg, or full opts as third for label override.
    undo:    (msg, onUndo, opts = {}) => push(opts.kind || 'info', msg, {
      ...opts,
      action: { label: opts.label || 'Rückgängig', onClick: onUndo },
    }),
    dismiss,
  }), [dismiss, push])

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="cc-toast-stack" aria-label="Benachrichtigungen">
        {toasts.map(t => {
          const Icon = ICONS[t.kind] || Info
          const tone = TONE[t.kind] || TONE.info
          return (
            <div
              key={t.id}
              role={t.kind === 'error' ? 'alert' : 'status'}
              aria-live={t.kind === 'error' ? 'assertive' : 'polite'}
              aria-atomic="true"
              className="cc-toast-item"
              style={{
                pointerEvents: 'auto',
                display: 'flex', alignItems: 'flex-start', gap: 10,
                background: '#ffffff',
                border: `1px solid ${tone.border}`,
                borderLeft: `4px solid ${tone.accent}`,
                borderRadius: 12,
                padding: '12px 14px',
                boxShadow: '0 12px 36px rgba(20,20,16,.12), 0 2px 6px rgba(20,20,16,.05)',
                animation: 'cc-toast-in 0.22s ease-out',
              }}
            >
              <Icon size={16} color={tone.accent} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} />
              <span style={{ flex: 1, fontSize: 13, color: '#1a1816', lineHeight: 1.45 }}>
                {t.message}
              </span>
              {t.action && (
                <button
                  onClick={() => {
                    try { t.action.onClick() } catch (e) { console.error('[toast.undo]', e) }
                    dismiss(t.id)
                  }}
                  style={{
                    background: '#fff1e8', border: '1px solid #ffd2b3',
                    color: '#b54708', fontWeight: 600, fontSize: 12,
                    padding: '4px 10px', borderRadius: 999, cursor: 'pointer',
                    flexShrink: 0, whiteSpace: 'nowrap',
                  }}
                >
                  {t.action.label}
                </button>
              )}
              <button
                onClick={() => dismiss(t.id)}
                  aria-label="Benachrichtigung schließen"
                style={{
                  background: 'transparent', border: 0, padding: 2,
                  color: '#9a968e', cursor: 'pointer', borderRadius: 4,
                  display: 'grid', placeItems: 'center', flexShrink: 0,
                }}
              >
                <X size={14} />
              </button>
            </div>
          )
        })}
      </div>
      <style>{`
        .cc-toast-stack {
          position: fixed;
          right: max(20px, env(safe-area-inset-right));
          bottom: max(20px, env(safe-area-inset-bottom));
          z-index: 10000;
          width: min(400px, calc(100vw - 32px));
          display: flex;
          flex-direction: column;
          gap: 10px;
          pointer-events: none;
          font-family: Inter, -apple-system, sans-serif;
        }
        .cc-toast-item { min-width: min(280px, 100%); }
        @keyframes cc-toast-in {
          from { opacity: 0; transform: translateY(8px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @media (max-width: 520px) {
          .cc-toast-stack {
            right: max(10px, env(safe-area-inset-right));
            bottom: max(10px, env(safe-area-inset-bottom));
            width: calc(100vw - 20px - env(safe-area-inset-left) - env(safe-area-inset-right));
          }
          .cc-toast-item { min-width: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .cc-toast-item { animation: none !important; }
        }
      `}</style>
    </ToastContext.Provider>
  )
}

export function useToast () {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    // Defensive: if used outside provider, return no-op so callsite doesn't crash.
    // (Console.warn helps catch the mistake in dev without breaking prod.)
    if (typeof console !== 'undefined') console.warn('[useToast] used outside ToastProvider, falling back to console')
    return {
      success: m => console.log('[toast.success]', m),
      error:   m => console.error('[toast.error]', m),
      warning: m => console.warn('[toast.warning]', m),
      info:    m => console.info('[toast.info]', m),
      dismiss: () => {},
    }
  }
  return ctx
}

export default ToastProvider
